/** Metrics — list view (server-side pagination/sort via the shared DataTable). */
import { useGo } from "@refinedev/core";
import { useTable } from "@refinedev/react-table";
import type { ColumnDef } from "@tanstack/react-table";
import type { HttpError } from "@refinedev/core";
import { useMemo } from "react";

import type { Metric } from "@aos/shared";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { PageHeader } from "@/components/layout/PageHeader";
import { RefLink } from "@/components/inspector";

import { DataTable, type RefineTableCore } from "../projects/_shared";

/** MetricList (D6) — generic CRUD list for metrics. */
export function MetricList() {
  const go = useGo();

  const columns = useMemo<ColumnDef<Metric, unknown>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => (
          <span className="font-medium text-foreground">{row.original.name}</span>
        ),
      },
      {
        id: "unit",
        accessorKey: "unit",
        header: "Unit",
        enableSorting: false,
        cell: ({ row }) =>
          row.original.unit ? (
            <span className="font-mono text-xs text-muted-foreground">
              {row.original.unit}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "description",
        accessorKey: "description",
        header: "Description",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="line-clamp-1 text-muted-foreground">
            {row.original.description?.trim() || "—"}
          </span>
        ),
      },
      {
        id: "owner_user_id",
        accessorKey: "owner_user_id",
        header: "Owner",
        enableSorting: false,
        cell: ({ row }) => (
          <span onClick={(e) => e.stopPropagation()}>
            <RefLink resource="users" id={row.original.owner_user_id} />
          </span>
        ),
      },
    ],
    [],
  );

  const table = useTable<Metric, HttpError>({
    columns,
    refineCoreProps: {
      resource: "metrics",
      sorters: { initial: [{ field: "created_at", order: "desc" }] },
      pagination: { pageSize: 25 },
    },
  });

  const core = table.refineCore as unknown as RefineTableCore;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Metrics"
        subtitle="Measurable signals tracked over time — each with a unit, owner, and recorded measurements."
        icon="okrs"
        actions={
          <Button
            size="sm"
            onClick={() => go({ to: { resource: "metrics", action: "create" } })}
          >
            <Icon name="create" size={16} className="mr-2" aria-hidden /> New metric
          </Button>
        }
      />

      <DataTable
        table={table}
        core={core}
        columns={columns}
        emptyMessage="No metrics yet."
        onRowClick={(m) =>
          go({ to: { resource: "metrics", action: "show", id: m.id } })
        }
      />
    </div>
  );
}

export default MetricList;
